import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useColorMode, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaNewspaper, FaBookOpen, FaFolderOpen, FaPlus, FaEdit,
  FaTrash, FaArrowRight, FaSignOutAlt, FaTachometerAlt,
  FaEye, FaHeart, FaClock,
  FaInbox, FaFileAlt,
  FaPhone, FaCheck, FaChevronDown, FaChevronUp,
  FaUsers, FaUserPlus, FaShieldAlt, FaLock, FaKey,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ROLE_PERMISSIONS = {
  superadmin:    ['blog', 'journal', 'ressources', 'inbox_contact', 'inbox_devis', 'inbox_cv', 'accounts'],
  rh:            ['inbox_cv'],
  commercial:    ['inbox_contact', 'inbox_devis'],
  communication: ['blog', 'journal', 'ressources'],
};

const ROLE_LABELS = {
  superadmin:    'Super Admin',
  rh:            'Ressources Humaines',
  commercial:    'Commercial',
  communication: 'Communication',
};

const ROLE_COLORS = {
  superadmin:    '#2563eb',
  rh:            '#059669',
  commercial:    '#d97706',
  communication: '#7c3aed',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "Aujourd'hui";
  if (d === 1) return 'Hier';
  if (d < 30) return `Il y a ${d} j`;
  const m = Math.floor(d / 30);
  if (m < 12) return `Il y a ${m} mois`;
  return `Il y a ${Math.floor(m / 12)} an(s)`;
}

function StatCard({ icon, label, value, color, loading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        flex: '1 1 180px', borderRadius: '16px', padding: '24px',
        background: `linear-gradient(135deg, ${color}18, ${color}08)`,
        border: `1px solid ${color}30`,
        display: 'flex', alignItems: 'center', gap: '16px',
      }}
    >
      <div style={{
        width: '48px', height: '48px', borderRadius: '12px',
        background: `${color}20`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', color, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '2rem', fontWeight: 900, color, lineHeight: 1 }}>
          {loading ? '—' : value}
        </div>
        <div style={{ fontSize: '0.8rem', fontWeight: 600, opacity: 0.7, marginTop: '4px' }}>{label}</div>
      </div>
    </motion.div>
  );
}

function RecentTable({ title, icon, items, loading, onDelete, editPath, color }) {
  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <span style={{ color, fontSize: '18px' }}>{icon}</span>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>{title}</h2>
      </div>
      <div style={{ borderRadius: '14px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', opacity: 0.4 }}>Chargement…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', opacity: 0.4 }}>Aucun contenu.</div>
        ) : items.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 18px',
              borderBottom: i < items.length - 1 ? '1px solid var(--border)' : 'none',
              background: 'var(--row-bg)', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--row-bg)'}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.title}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: '2px' }}>{timeAgo(item.createdAt)}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <RouterLink
                to={`${editPath}/${item.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '6px 12px', borderRadius: '8px',
                  background: `${color}15`, color, fontSize: '12px',
                  fontWeight: 700, textDecoration: 'none', border: `1px solid ${color}30`,
                }}
              >
                <FaEdit size={11} /> Éditer
              </RouterLink>
              <button
                onClick={() => onDelete(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '6px 12px', borderRadius: '8px',
                  background: '#ef444415', color: '#ef4444', fontSize: '12px',
                  fontWeight: 700, border: '1px solid #ef444430', cursor: 'pointer',
                }}
              >
                <FaTrash size={11} /> Suppr.
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const adminRoles = (() => {
    try { return JSON.parse(localStorage.getItem('adminRoles') || '["superadmin"]'); }
    catch { return ['superadmin']; }
  })();
  const adminNom = localStorage.getItem('adminNom') || '';
  const adminId = parseInt(localStorage.getItem('adminId') || '0');

  const can = (perm) => adminRoles.some(r => (ROLE_PERMISSIONS[r] || []).includes(perm));
  const showContent = can('blog') || can('journal') || can('ressources');
  const showInbox = can('inbox_contact') || can('inbox_devis') || can('inbox_cv');

  const [blogs, setBlogs] = useState([]);
  const [journals, setJournals] = useState([]);
  const [ressources, setRessources] = useState([]);
  const [loading, setLoading] = useState(true);

  const [messages, setMessages] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // Gestion des comptes (superadmin)
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [newAccount, setNewAccount] = useState({ email: '', nom: '', mdp: '', roles: ['communication'] });
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [showMdp, setShowMdp] = useState(false);

  // Demandes en attente
  const [pending, setPending] = useState([]);
  const [approveModal, setApproveModal] = useState(null);
  const [approveRoles, setApproveRoles] = useState([]);

  // Édition d'un compte existant
  const [editModal, setEditModal] = useState(null); // { id, nom, email, roles }
  const [editRoles, setEditRoles] = useState([]);
  const [editMdp, setEditMdp] = useState('');
  const [editShowMdp, setEditShowMdp] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const refreshAccounts = async () => {
    const r = await axios.get(`${API_URL}/api/auth/accounts`, { headers: { Authorization: `Bearer ${token}` } });
    setAccounts(r.data || []);
  };

  const refreshPending = async () => {
    const r = await axios.get(`${API_URL}/api/auth/pending`, { headers: { Authorization: `Bearer ${token}` } });
    setPending(r.data || []);
  };

  const handleApprove = async () => {
    if (approveRoles.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/auth/pending/${approveModal.id}/approve`, { roles: approveRoles }, { headers: { Authorization: `Bearer ${token}` } });
      setApproveModal(null);
      await Promise.all([refreshPending(), refreshAccounts()]);
    } catch (err) { alert(err.response?.data?.message || 'Erreur.'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter et supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API_URL}/api/auth/pending/${id}/reject`, { headers: { Authorization: `Bearer ${token}` } });
      await refreshPending();
    } catch (err) { alert(err.response?.data?.message || 'Erreur.'); }
  };

  const openEditModal = (acc) => {
    setEditModal(acc);
    setEditRoles(Array.isArray(acc.roles) ? [...acc.roles] : ['communication']);
    setEditMdp('');
    setEditError('');
    setEditSuccess('');
  };

  const handleSaveRoles = async () => {
    if (editRoles.length === 0) { setEditError('Au moins un rôle est requis.'); return; }
    setEditLoading(true); setEditError(''); setEditSuccess('');
    try {
      await axios.patch(`${API_URL}/api/auth/accounts/${editModal.id}/roles`, { roles: editRoles }, { headers: { Authorization: `Bearer ${token}` } });
      setEditSuccess('Rôles mis à jour.');
      await refreshAccounts();
    } catch (err) { setEditError(err.response?.data?.message || 'Erreur.'); }
    finally { setEditLoading(false); }
  };

  const handleSavePassword = async () => {
    if (!editMdp) { setEditError('Entrez un nouveau mot de passe.'); return; }
    setEditLoading(true); setEditError(''); setEditSuccess('');
    try {
      await axios.patch(`${API_URL}/api/auth/accounts/${editModal.id}/password`, { mdp: editMdp }, { headers: { Authorization: `Bearer ${token}` } });
      setEditSuccess('Mot de passe mis à jour.'); setEditMdp('');
    } catch (err) { setEditError(err.response?.data?.message || 'Erreur.'); }
    finally { setEditLoading(false); }
  };

  useEffect(() => {
    if (!token) { navigate('/connexion'); return; }
    const headers = { Authorization: `Bearer ${token}` };

    if (showContent) {
      Promise.all([
        can('blog') ? axios.get(`${API_URL}/api/blog`) : Promise.resolve({ data: [] }),
        can('journal') ? axios.get(`${API_URL}/api/journal`) : Promise.resolve({ data: [] }),
        can('ressources') ? axios.get(`${API_URL}/api/ressources/recuperer`) : Promise.resolve({ data: [] }),
      ]).then(([b, j, r]) => {
        setBlogs(b.data || []);
        setJournals(j.data || []);
        setRessources(r.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    if (showInbox) {
      Promise.all([
        (can('inbox_contact') || can('inbox_devis'))
          ? axios.get(`${API_URL}/api/contact/admin/messages`, { headers })
          : Promise.resolve({ data: [] }),
        can('inbox_cv')
          ? axios.get(`${API_URL}/api/contact/admin/candidatures`, { headers })
          : Promise.resolve({ data: [] }),
      ]).then(([m, c]) => {
        setMessages(m.data || []);
        setCandidatures(c.data || []);
      }).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('adminRoles');
          localStorage.removeItem('adminRole');
          localStorage.removeItem('adminNom');
          localStorage.removeItem('adminId');
          navigate('/connexion');
        }
      }).finally(() => setInboxLoading(false));
    } else {
      setInboxLoading(false);
    }

    if (can('accounts')) {
      setAccountsLoading(true);
      Promise.all([
        axios.get(`${API_URL}/api/auth/accounts`, { headers }),
        axios.get(`${API_URL}/api/auth/pending`, { headers }),
      ]).then(([acc, pend]) => {
        setAccounts(acc.data || []);
        setPending(pend.data || []);
      }).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/connexion');
        }
      }).finally(() => setAccountsLoading(false));
    }
  }, [token, navigate]); // eslint-disable-line

  const markMessageSeen = async (id) => {
    await axios.patch(`${API_URL}/api/contact/admin/messages/${id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setMessages(prev => prev.map(m => m.id === id ? { ...m, seen: true } : m));
  };

  const markCandidatureSeen = async (id) => {
    await axios.patch(`${API_URL}/api/contact/admin/candidatures/${id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setCandidatures(prev => prev.map(c => c.id === id ? { ...c, seen: true } : c));
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    await axios.delete(`${API_URL}/api/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setBlogs(prev => prev.filter(b => b.id !== id));
  };

  const handleDeleteJournal = async (id) => {
    if (!window.confirm('Supprimer ce journal ?')) return;
    await axios.delete(`${API_URL}/api/journal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setJournals(prev => prev.filter(j => j.id !== id));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminRoles');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminNom');
    localStorage.removeItem('adminId');
    navigate('/');
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    setAccountSuccess('');
    setCreatingAccount(true);
    try {
      await axios.post(`${API_URL}/api/auth/accounts`, { ...newAccount, roles: newAccount.roles }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccountSuccess(`Compte "${newAccount.nom || newAccount.email}" créé avec succès.`);
      setNewAccount({ email: '', nom: '', mdp: '', roles: ['communication'] });
      await refreshAccounts();
    } catch (err) {
      setAccountError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Supprimer ce compte ?')) return;
    try {
      await axios.delete(`${API_URL}/api/auth/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  };

  const bg = isDark ? '#0a0f16' : '#f0f4f8';
  const card = isDark ? '#111827' : '#ffffff';
  const text = isDark ? '#f1f5f9' : '#0f172a';
  const border = isDark ? '#1e293b' : '#e2e8f0';

  return (
    <Box
      minH="100vh" bg={bg} color={text}
      style={{ '--border': border, '--row-bg': card, '--row-hover': isDark ? '#1a2535' : '#f8faff' }}
    >
      <Helmet><title>Tableau de bord — Administration HIS</title></Helmet>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: isDark ? 'rgba(10,15,22,0.95)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${border}`,
        padding: '0 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: '60px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 800, fontSize: '1rem', color: '#2563eb' }}>
          <FaTachometerAlt /> Tableau de bord
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {adminRoles.map(r => (
              <span key={r} style={{
                fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px',
                background: `${ROLE_COLORS[r] || '#64748b'}18`,
                color: ROLE_COLORS[r] || '#64748b',
                border: `1px solid ${ROLE_COLORS[r] || '#64748b'}30`,
              }}>
                {ROLE_LABELS[r] || r}
              </span>
            ))}
          </div>
          <RouterLink to="/" style={{ fontSize: '13px', color: text, opacity: 0.6, textDecoration: 'none', fontWeight: 600 }}>
            ← Voir le site
          </RouterLink>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 14px', borderRadius: '8px',
              background: '#ef444415', color: '#ef4444',
              border: '1px solid #ef444430', fontSize: '13px',
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            <FaSignOutAlt size={12} /> Déconnexion
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Titre */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: '32px' }}>
          <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 900 }}>
            Bonjour {adminNom ? adminNom.split(' ')[0] : ''} 👋
          </h1>
          <p style={{ margin: '6px 0 0', opacity: 0.5, fontSize: '0.9rem' }}>
            Gérez votre contenu depuis cet espace ·{' '}
            {adminRoles.map((r, i) => (
              <span key={r} style={{ color: ROLE_COLORS[r] || '#64748b', fontWeight: 700 }}>
                {i > 0 ? ', ' : ''}{ROLE_LABELS[r] || r}
              </span>
            ))}
          </p>
        </motion.div>

        {/* Stats */}
        {showContent && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '40px' }}>
            {can('blog') && <StatCard icon={<FaNewspaper size={20} />} label="Articles publiés" value={blogs.length} color="#2563eb" loading={loading} />}
            {can('journal') && <StatCard icon={<FaBookOpen size={20} />} label="Journaux" value={journals.length} color="#7c3aed" loading={loading} />}
            {can('ressources') && <StatCard icon={<FaFolderOpen size={20} />} label="Ressources" value={ressources.length} color="#059669" loading={loading} />}
          </div>
        )}

        {/* Actions rapides */}
        {showContent && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '0.75rem', fontWeight: 800, marginBottom: '14px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Actions rapides
            </h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {can('blog') && (
                <RouterLink to="/ajouter-article" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', background: '#2563eb15', border: '1px solid #2563eb30', color: '#2563eb', fontWeight: 700, fontSize: '14px', textDecoration: 'none' }}>
                  <FaPlus size={11} /> Nouvel article
                </RouterLink>
              )}
              {can('journal') && (
                <RouterLink to="/ajouter-journal" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', background: '#7c3aed15', border: '1px solid #7c3aed30', color: '#7c3aed', fontWeight: 700, fontSize: '14px', textDecoration: 'none' }}>
                  <FaPlus size={11} /> Nouveau journal
                </RouterLink>
              )}
              {can('ressources') && (
                <RouterLink to="/RessourcesForm" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '10px', background: '#05966915', border: '1px solid #05966930', color: '#059669', fontWeight: 700, fontSize: '14px', textDecoration: 'none' }}>
                  <FaPlus size={11} /> Nouvelle ressource
                </RouterLink>
              )}
            </div>
          </motion.div>
        )}

        {/* Contenu récent */}
        {showContent && (
          <div style={{ background: card, borderRadius: '20px', border: `1px solid ${border}`, padding: '28px', marginBottom: '32px' }}>
            {can('blog') && (
              <RecentTable
                title="Articles récents" icon={<FaNewspaper />}
                items={[...blogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6)}
                loading={loading} onDelete={handleDeleteBlog} editPath="/modifier-article" color="#2563eb"
              />
            )}
            {can('journal') && (
              <RecentTable
                title="Journaux récents" icon={<FaBookOpen />}
                items={[...journals].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4)}
                loading={loading} onDelete={handleDeleteJournal} editPath="/modifier-journal" color="#7c3aed"
              />
            )}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
              {can('blog') && <RouterLink to="/blog" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>Voir tous les articles <FaArrowRight size={10} /></RouterLink>}
              {can('journal') && <RouterLink to="/journal" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#7c3aed', textDecoration: 'none', fontWeight: 600 }}>Voir tous les journaux <FaArrowRight size={10} /></RouterLink>}
              {can('ressources') && <RouterLink to="/NosRessources" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#059669', textDecoration: 'none', fontWeight: 600 }}>Voir les ressources <FaArrowRight size={10} /></RouterLink>}
            </div>
          </div>
        )}

        {/* ── BOÎTE DE RÉCEPTION ── */}
        {showInbox && (() => {
          const allItems = [
            ...(can('inbox_contact') || can('inbox_devis') ? messages.filter(m => {
              if (m.type === 'CONTACT' && can('inbox_contact')) return true;
              if (m.type === 'DEVIS' && can('inbox_devis')) return true;
              if (m.type === 'RAPPEL_RAPIDE' && can('inbox_contact')) return true;
              return false;
            }).map(m => ({ ...m, _kind: m.type === 'DEVIS' ? 'devis' : 'contact' })) : []),
            ...(can('inbox_cv') ? candidatures.map(c => ({ ...c, _kind: 'cv' })) : []),
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          const unreadCount = allItems.filter(i => !i.seen).length;

          const filtered = allItems.filter(i => {
            if (activeTab !== 'all' && i._kind !== activeTab) return false;
            if (onlyUnread && i.seen) return false;
            return true;
          });

          const tabDef = [
            { key: 'all', label: 'Tout', color: '#2563eb', count: allItems.length },
            ...(can('inbox_contact') ? [{ key: 'contact', label: 'Contacts', color: '#0891b2', count: messages.filter(m => m.type === 'CONTACT' || m.type === 'RAPPEL_RAPIDE').length }] : []),
            ...(can('inbox_devis') ? [{ key: 'devis', label: 'Devis', color: '#d97706', count: messages.filter(m => m.type === 'DEVIS').length }] : []),
            ...(can('inbox_cv') ? [{ key: 'cv', label: 'CV', color: '#059669', count: candidatures.length }] : []),
          ];

          const kindStyle = {
            contact: { label: 'Contact', bg: '#0891b215', border: '#0891b230', color: '#0891b2', dot: '#0891b2' },
            devis:   { label: 'Devis',   bg: '#d9770615', border: '#d9770630', color: '#d97706', dot: '#d97706' },
            cv:      { label: 'CV',      bg: '#05966915', border: '#05966930', color: '#059669', dot: '#059669' },
          };

          const initials = (item) => {
            if (item._kind === 'cv') return (item.jobTitle || 'CV')[0].toUpperCase();
            return `${(item.prenom || '')[0] || ''}${(item.nom || '')[0] || ''}`.toUpperCase();
          };

          const displayName = (item) =>
            item._kind === 'cv' ? item.jobTitle : `${item.prenom || ''} ${item.nom || ''}`.trim();

          return (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              style={{ marginBottom: '32px', background: card, borderRadius: '20px', border: `1px solid ${border}`, overflow: 'hidden' }}
            >
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaInbox style={{ color: '#2563eb', fontSize: '18px' }} />
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Boîte de réception</h2>
                  {unreadCount > 0 && (
                    <span style={{ background: '#ef4444', color: 'white', borderRadius: '999px', fontSize: '11px', fontWeight: 800, padding: '2px 8px', lineHeight: 1.5 }}>
                      {unreadCount} non lu{unreadCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setOnlyUnread(v => !v)}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    background: onlyUnread ? '#2563eb' : 'transparent',
                    color: onlyUnread ? 'white' : text,
                    border: `1px solid ${onlyUnread ? '#2563eb' : border}`,
                  }}
                >
                  {onlyUnread ? '● Non lus seulement' : '○ Afficher non lus'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: '4px', padding: '12px 16px', borderBottom: `1px solid ${border}`, overflowX: 'auto' }}>
                {tabDef.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      background: activeTab === tab.key ? `${tab.color}18` : 'transparent',
                      color: activeTab === tab.key ? tab.color : (isDark ? '#94a3b8' : '#64748b'),
                      border: activeTab === tab.key ? `1px solid ${tab.color}40` : '1px solid transparent',
                    }}
                  >
                    {tab.label} <span style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.7 }}>({tab.count})</span>
                  </button>
                ))}
              </div>

              {inboxLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', opacity: 0.4 }}>Chargement…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <FaInbox style={{ fontSize: '32px', opacity: 0.15, marginBottom: '12px' }} />
                  <p style={{ opacity: 0.4, fontSize: '14px', margin: 0 }}>Aucun message dans cette catégorie.</p>
                </div>
              ) : filtered.map((item, i) => {
                const ks = kindStyle[item._kind];
                const isExpanded = expanded === `${item._kind}-${item.id}`;
                const isLast = i === filtered.length - 1;
                return (
                  <div key={`${item._kind}-${item.id}`} style={{ borderBottom: isLast ? 'none' : `1px solid ${border}` }}>
                    <div
                      onClick={() => {
                        const key = `${item._kind}-${item.id}`;
                        setExpanded(isExpanded ? null : key);
                        if (!item.seen) {
                          if (item._kind === 'cv') markCandidatureSeen(item.id);
                          else markMessageSeen(item.id);
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        padding: '14px 20px', cursor: 'pointer',
                        background: item.seen ? 'transparent' : (isDark ? '#1e293b' : '#f0f7ff'),
                        borderLeft: `3px solid ${item.seen ? 'transparent' : ks.dot}`,
                        opacity: item.seen ? 0.55 : 1,
                      }}
                      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = isDark ? '#1a2535' : '#e8f0fe'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = item.seen ? 'transparent' : (isDark ? '#1e293b' : '#f0f7ff'); }}
                    >
                      <div style={{ width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0, background: ks.bg, border: `1px solid ${ks.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: ks.color }}>
                        {initials(item)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: item.seen ? 600 : 800, fontSize: '0.9rem' }}>{displayName(item)}</span>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: ks.bg, color: ks.color, border: `1px solid ${ks.border}` }}>{ks.label}</span>
                          {!item.seen && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: ks.dot, flexShrink: 0 }} />}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.5, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.email}
                          {item._kind !== 'cv' && item.message ? ` — "${item.message.slice(0, 60)}${item.message.length > 60 ? '…' : ''}"` : ''}
                          {item._kind === 'cv' && item.cvFilename ? ` — ${item.cvFilename}` : ''}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <span style={{ fontSize: '11px', opacity: 0.45, whiteSpace: 'nowrap' }}>
                          {new Date(item.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {item.seen ? <FaCheck size={10} style={{ color: '#22c55e', opacity: 0.6 }} /> : (isExpanded ? <FaChevronUp size={10} style={{ opacity: 0.4 }} /> : <FaChevronDown size={10} style={{ opacity: 0.4 }} />)}
                      </div>
                    </div>

                    {isExpanded && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        style={{ padding: '16px 20px 20px', borderTop: `1px solid ${border}`, background: isDark ? '#0f1929' : '#f8fbff' }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', fontSize: '13px' }}>
                          {item._kind !== 'cv' && (
                            <>
                              <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Nom complet</div><div style={{ fontWeight: 600 }}>{item.prenom} {item.nom}</div></div>
                              {item.telephone && <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Téléphone</div><a href={`tel:${item.telephone}`} style={{ fontWeight: 700, color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}><FaPhone size={10} /> {item.telephone}</a></div>}
                              <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Email</div><a href={`mailto:${item.email}`} style={{ fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>{item.email}</a></div>
                              {item.services && <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Services</div><div style={{ fontWeight: 600 }}>{item.services}</div></div>}
                              {item.date_souhaitee && <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>RDV souhaité</div><div style={{ fontWeight: 600 }}>{item.date_souhaitee}</div></div>}
                            </>
                          )}
                          {item._kind === 'cv' && (
                            <>
                              <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Poste visé</div><div style={{ fontWeight: 600 }}>{item.jobTitle}</div></div>
                              {item.phone && <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Téléphone</div><a href={`tel:${item.phone}`} style={{ fontWeight: 700, color: '#059669', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}><FaPhone size={10} /> {item.phone}</a></div>}
                              <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Email</div><a href={`mailto:${item.email}`} style={{ fontWeight: 600, color: '#059669', textDecoration: 'none' }}>{item.email}</a></div>
                              {item.cvFilename && <div><div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Fichier CV</div><div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}><FaFileAlt size={10} style={{ color: '#059669' }} /> {item.cvFilename}<a href={`${API_URL}/api/contact/admin/candidatures/${item.id}/cv`} download target="_blank" rel="noreferrer" onClick={e => { e.stopPropagation(); fetch(`${API_URL}/api/contact/admin/candidatures/${item.id}/cv`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.blob() : Promise.reject()).then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = item.cvFilename; a.click(); URL.revokeObjectURL(url); }).catch(() => alert('Fichier non disponible.')); e.preventDefault(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '6px', background: '#05966915', color: '#059669', border: '1px solid #05966930', fontSize: '11px', fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}><FaFileAlt size={9} /> Télécharger</a></div></div>}
                            </>
                          )}
                        </div>
                        {item._kind !== 'cv' && item.message && (
                          <div style={{ marginTop: '14px', padding: '12px 16px', borderRadius: '10px', background: isDark ? '#1e293b' : '#eef4ff', borderLeft: `3px solid ${ks.dot}` }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Message</div>
                            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, fontStyle: 'italic' }}>{item.message}</p>
                          </div>
                        )}
                        <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
                          <span style={{ fontSize: '11px', opacity: 0.35 }}>
                            Reçu le {new Date(item.createdAt).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} à {new Date(item.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </motion.div>
          );
        })()}

        {/* Stats articles */}
        {can('blog') && !loading && blogs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            style={{ marginBottom: '24px', background: card, borderRadius: '20px', border: `1px solid ${border}`, padding: '28px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <FaNewspaper style={{ color: '#2563eb' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Statistiques des articles</h2>
              <div style={{ display: 'flex', gap: '20px', marginLeft: 'auto', fontSize: '11px', fontWeight: 700, opacity: 0.5 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaEye /> Vues</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaHeart /> Likes</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaClock /> Lecture</span>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${border}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Titre</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Vues</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Likes</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Lecture</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Publié</th>
                  </tr>
                </thead>
                <tbody>
                  {[...blogs].sort((a, b) => (b.views || 0) - (a.views || 0)).map((blog, i) => {
                    const words = (blog.content || '').replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
                    const readMin = Math.max(1, Math.round(words / 200));
                    return (
                      <tr key={blog.id} style={{ borderBottom: i < blogs.length - 1 ? `1px solid ${border}` : 'none' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? '#1a2535' : '#f8faff'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '10px 12px', maxWidth: '260px' }}><div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{blog.title}</div></td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700, color: '#2563eb' }}><FaEye size={11} /> {blog.views || 0}</span></td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700, color: '#e11d48' }}><FaHeart size={11} /> {blog.likes || 0}</span></td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', opacity: 0.6, fontSize: '12px' }}>{readMin} min</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', opacity: 0.5, fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(blog.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Stats journaux */}
        {can('journal') && !loading && journals.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            style={{ marginBottom: '24px', background: card, borderRadius: '20px', border: `1px solid ${border}`, padding: '28px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <FaBookOpen style={{ color: '#7c3aed' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Statistiques des journaux</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${border}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Titre</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Période</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, opacity: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>Publié</th>
                  </tr>
                </thead>
                <tbody>
                  {[...journals].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((journal, i) => (
                    <tr key={journal.id} style={{ borderBottom: i < journals.length - 1 ? `1px solid ${border}` : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = isDark ? '#1a2535' : '#f8faff'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '10px 12px', maxWidth: '260px' }}><div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{journal.title}</div></td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600, color: '#7c3aed', fontSize: '12px', background: '#7c3aed15', border: '1px solid #7c3aed30', borderRadius: '6px', padding: '2px 8px' }}>{journal.period || '—'}</span></td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', opacity: 0.5, fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(journal.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* ── GESTION DES COMPTES (superadmin) ── */}
        {can('accounts') && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            style={{ background: card, borderRadius: '20px', border: `1px solid ${border}`, overflow: 'hidden' }}
          >
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaUsers style={{ color: '#2563eb', fontSize: '18px' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Gestion des comptes</h2>
              <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', background: '#2563eb18', color: '#2563eb', border: '1px solid #2563eb30' }}>
                {accounts.length} compte{accounts.length > 1 ? 's' : ''}
              </span>
            </div>

            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>

              {/* Formulaire création */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <FaUserPlus style={{ color: '#2563eb' }} />
                  <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>Créer un compte</h3>
                </div>
                <form onSubmit={handleCreateAccount} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <input
                    type="text" placeholder="Nom et prénom" value={newAccount.nom}
                    onChange={e => setNewAccount(p => ({ ...p, nom: e.target.value }))}
                    maxLength={60}
                    style={{ padding: '10px 14px', borderRadius: '10px', border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#f8faff', color: text, fontSize: '14px', outline: 'none' }}
                  />
                  <input
                    type="email" placeholder="Adresse email *" value={newAccount.email}
                    onChange={e => setNewAccount(p => ({ ...p, email: e.target.value }))}
                    required maxLength={100}
                    style={{ padding: '10px 14px', borderRadius: '10px', border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#f8faff', color: text, fontSize: '14px', outline: 'none' }}
                  />
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showMdp ? 'text' : 'password'} placeholder="Mot de passe * (12+ car., MAJ, chiffre, @)" value={newAccount.mdp}
                      onChange={e => setNewAccount(p => ({ ...p, mdp: e.target.value }))}
                      required
                      style={{ width: '100%', padding: '10px 40px 10px 14px', borderRadius: '10px', border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#f8faff', color: text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={() => setShowMdp(v => !v)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: text, opacity: 0.4, fontSize: '14px' }}>
                      {showMdp ? '🙈' : '👁'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rôles *</div>
                    {[
                      { value: 'communication', label: 'Communication', desc: 'Blog, Journal, Ressources' },
                      { value: 'commercial', label: 'Commercial', desc: 'Contacts & Devis' },
                      { value: 'rh', label: 'RH', desc: 'CV uniquement' },
                      { value: 'superadmin', label: 'Super Admin', desc: 'Accès complet' },
                    ].map(({ value, label, desc }) => {
                      const checked = newAccount.roles.includes(value);
                      const c = ROLE_COLORS[value];
                      return (
                        <label key={value} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '9px 12px', borderRadius: '10px', cursor: 'pointer',
                          background: checked ? `${c}12` : (isDark ? '#1e293b' : '#f8faff'),
                          border: `1px solid ${checked ? c + '40' : border}`,
                          transition: 'all 0.15s',
                        }}>
                          <input type="checkbox" checked={checked}
                            onChange={() => setNewAccount(p => ({
                              ...p,
                              roles: checked ? p.roles.filter(r => r !== value) : [...p.roles, value],
                            }))}
                            style={{ accentColor: c, width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div>
                            <span style={{ fontWeight: 700, fontSize: '13px', color: checked ? c : text }}>{label}</span>
                            <span style={{ fontSize: '11px', opacity: 0.5, marginLeft: '6px' }}>— {desc}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {accountError && (
                    <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', fontSize: '13px', fontWeight: 600 }}>
                      {accountError}
                    </div>
                  )}
                  {accountSuccess && (
                    <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#22c55e15', border: '1px solid #22c55e30', color: '#22c55e', fontSize: '13px', fontWeight: 600 }}>
                      {accountSuccess}
                    </div>
                  )}

                  <button type="submit" disabled={creatingAccount}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      padding: '11px 20px', borderRadius: '10px',
                      background: creatingAccount ? '#94a3b8' : '#2563eb', color: 'white',
                      fontWeight: 700, fontSize: '14px', border: 'none', cursor: creatingAccount ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <FaUserPlus size={13} /> {creatingAccount ? 'Création…' : 'Créer le compte'}
                  </button>
                </form>
              </div>

              {/* Liste des comptes */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <FaShieldAlt style={{ color: '#7c3aed' }} />
                  <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>Comptes existants</h3>
                </div>
                {accountsLoading ? (
                  <div style={{ opacity: 0.4, fontSize: '14px' }}>Chargement…</div>
                ) : accounts.length === 0 ? (
                  <div style={{ opacity: 0.4, fontSize: '14px' }}>Aucun compte.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {accounts.map(acc => {
                      const accRoles = Array.isArray(acc.roles) ? acc.roles : (acc.role ? [acc.role] : ['communication']);
                      const rc = ROLE_COLORS[accRoles[0]] || '#64748b';
                      const isSelf = acc.id === adminId;
                      return (
                        <div key={acc.id} style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '12px 14px', borderRadius: '12px',
                          background: isDark ? '#1e293b' : '#f8faff',
                          border: isSelf ? `1px solid ${rc}40` : `1px solid ${border}`,
                        }}>
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                            background: `${rc}20`, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontWeight: 800, fontSize: '13px', color: rc,
                          }}>
                            {(acc.nom || acc.email)[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {acc.nom || acc.email}
                              {isSelf && <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: `${rc}20`, color: rc }}>Vous</span>}
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.5 }}>{acc.email}</div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {accRoles.map(r => (
                                <span key={r} style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: `${ROLE_COLORS[r] || '#64748b'}15`, color: ROLE_COLORS[r] || '#64748b', border: `1px solid ${ROLE_COLORS[r] || '#64748b'}30`, whiteSpace: 'nowrap' }}>
                                  {ROLE_LABELS[r] || r}
                                </span>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => openEditModal(acc)}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: '#2563eb15', color: '#2563eb', fontSize: '11px', fontWeight: 700, border: '1px solid #2563eb30', cursor: 'pointer' }}
                              >
                                <FaEdit size={9} /> Éditer
                              </button>
                              {!isSelf && (
                                <button
                                  onClick={() => handleDeleteAccount(acc.id)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: '#ef444415', color: '#ef4444', fontSize: '11px', fontWeight: 700, border: '1px solid #ef444430', cursor: 'pointer' }}
                                >
                                  <FaTrash size={9} /> Suppr.
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Demandes en attente */}
            {pending.length > 0 && (
              <div style={{ padding: '0 24px 24px', borderBottom: `1px solid ${border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingTop: '4px' }}>
                  <span style={{ fontSize: '18px' }}>⏳</span>
                  <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>Demandes en attente</h3>
                  <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: '#f9731615', color: '#f97316', border: '1px solid #f9731630' }}>
                    {pending.length} en attente
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {pending.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', background: isDark ? '#1e293b' : '#fff7ed', border: '1px solid #f9731625' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0, background: '#f9731620', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '13px', color: '#f97316' }}>
                        {(p.nom || p.email)[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '14px' }}>{p.nom || '—'}</div>
                        <div style={{ fontSize: '12px', opacity: 0.5 }}>{p.email}</div>
                        {p.requested_roles && p.requested_roles.length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                            {p.requested_roles.map(r => (
                              <span key={r} style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: `${ROLE_COLORS[r] || '#64748b'}15`, color: ROLE_COLORS[r] || '#64748b', border: `1px solid ${ROLE_COLORS[r] || '#64748b'}30` }}>
                                {ROLE_LABELS[r] || r} (souhaité)
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button onClick={() => { setApproveModal(p); setApproveRoles(p.requested_roles || ['communication']); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '7px', background: '#22c55e15', color: '#22c55e', fontSize: '11px', fontWeight: 700, border: '1px solid #22c55e30', cursor: 'pointer' }}>
                          ✓ Approuver
                        </button>
                        <button onClick={() => handleReject(p.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '7px', background: '#ef444415', color: '#ef4444', fontSize: '11px', fontWeight: 700, border: '1px solid #ef444430', cursor: 'pointer' }}>
                          ✕ Rejeter
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info rôles */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${border}`, background: isDark ? '#0f1929' : '#f0f7ff' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaLock size={10} /> Accès par rôle
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(ROLE_LABELS).map(([role, label]) => (
                  <div key={role} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '8px', background: `${ROLE_COLORS[role]}10`, border: `1px solid ${ROLE_COLORS[role]}25` }}>
                    <span style={{ fontWeight: 800, color: ROLE_COLORS[role] }}>{label}</span>
                    <span style={{ opacity: 0.5 }}>—</span>
                    <span style={{ opacity: 0.7, fontSize: '11px' }}>
                      {(ROLE_PERMISSIONS[role] || []).map(p => ({
                        blog: 'Blog', journal: 'Journal', ressources: 'Ressources',
                        inbox_contact: 'Contacts', inbox_devis: 'Devis', inbox_cv: 'CV', accounts: 'Comptes'
                      }[p])).filter(Boolean).join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

      </div>

      {/* Modal édition compte */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            style={{ background: card, borderRadius: '20px', border: `1px solid ${border}`, padding: '28px', maxWidth: '440px', width: '100%' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: 900 }}>Modifier le compte</h3>
                <p style={{ margin: 0, fontSize: '13px', opacity: 0.5 }}>{editModal.nom || editModal.email}</p>
              </div>
              <button onClick={() => setEditModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', opacity: 0.4, color: text, lineHeight: 1 }}>✕</button>
            </div>

            {/* Section rôles */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaShieldAlt size={10} /> Rôles
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { value: 'communication', label: 'Communication', desc: 'Blog, Journal, Ressources' },
                  { value: 'commercial', label: 'Commercial', desc: 'Contacts & Devis' },
                  { value: 'rh', label: 'RH', desc: 'CV uniquement' },
                  { value: 'superadmin', label: 'Super Admin', desc: 'Accès complet' },
                ].map(({ value, label, desc }) => {
                  const checked = editRoles.includes(value);
                  const c = ROLE_COLORS[value];
                  return (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '10px', cursor: 'pointer', background: checked ? `${c}12` : (isDark ? '#1e293b' : '#f8faff'), border: `1px solid ${checked ? c + '40' : border}`, transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={checked}
                        onChange={() => setEditRoles(prev => checked ? prev.filter(r => r !== value) : [...prev, value])}
                        style={{ accentColor: c, width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <div>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: checked ? c : text }}>{label}</span>
                        <span style={{ fontSize: '11px', opacity: 0.5, marginLeft: '6px' }}>— {desc}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
              <button onClick={handleSaveRoles} disabled={editLoading || editRoles.length === 0}
                style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '10px', background: editRoles.length === 0 ? '#94a3b8' : '#2563eb', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', cursor: editRoles.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FaShieldAlt size={11} /> {editLoading ? 'Sauvegarde…' : 'Enregistrer les rôles'}
              </button>
            </div>

            <div style={{ borderTop: `1px solid ${border}`, paddingTop: '20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaKey size={10} /> Nouveau mot de passe
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type={editShowMdp ? 'text' : 'password'}
                  placeholder="Nouveau mot de passe (12+ car., MAJ, chiffre, @)"
                  value={editMdp}
                  onChange={e => setEditMdp(e.target.value)}
                  style={{ width: '100%', padding: '10px 40px 10px 14px', borderRadius: '10px', border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#f8faff', color: text, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setEditShowMdp(v => !v)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: text, opacity: 0.4, fontSize: '14px' }}>
                  {editShowMdp ? '🙈' : '👁'}
                </button>
              </div>
              <button onClick={handleSavePassword} disabled={editLoading || !editMdp}
                style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '10px', background: !editMdp ? '#94a3b8' : '#7c3aed', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', cursor: !editMdp ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <FaKey size={11} /> {editLoading ? 'Sauvegarde…' : 'Changer le mot de passe'}
              </button>
            </div>

            {editError && <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>{editError}</div>}
            {editSuccess && <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#22c55e15', border: '1px solid #22c55e30', color: '#22c55e', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>{editSuccess}</div>}
          </motion.div>
        </div>
      )}

      {/* Modal approbation */}
      {approveModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            style={{ background: card, borderRadius: '20px', border: `1px solid ${border}`, padding: '28px', maxWidth: '420px', width: '100%' }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: 900 }}>Approuver le compte</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', opacity: 0.5 }}>
              {approveModal.nom} — {approveModal.email}
            </p>
            <div style={{ fontSize: '12px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Rôles à attribuer *</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
              {[
                { value: 'communication', label: 'Communication', desc: 'Blog, Journal, Ressources' },
                { value: 'commercial', label: 'Commercial', desc: 'Contacts & Devis' },
                { value: 'rh', label: 'RH', desc: 'CV uniquement' },
                { value: 'superadmin', label: 'Super Admin', desc: 'Accès complet' },
              ].map(({ value, label, desc }) => {
                const checked = approveRoles.includes(value);
                const c = ROLE_COLORS[value];
                return (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '10px', cursor: 'pointer', background: checked ? `${c}12` : (isDark ? '#1e293b' : '#f8faff'), border: `1px solid ${checked ? c + '40' : border}`, transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => setApproveRoles(prev => checked ? prev.filter(r => r !== value) : [...prev, value])}
                      style={{ accentColor: c, width: '15px', height: '15px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '13px', color: checked ? c : text }}>{label}</span>
                      <span style={{ fontSize: '11px', opacity: 0.5, marginLeft: '6px' }}>— {desc}</span>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleApprove} disabled={approveRoles.length === 0}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: approveRoles.length === 0 ? '#94a3b8' : '#22c55e', color: 'white', fontWeight: 700, fontSize: '14px', border: 'none', cursor: approveRoles.length === 0 ? 'not-allowed' : 'pointer' }}>
                ✓ Valider
              </button>
              <button onClick={() => setApproveModal(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: isDark ? '#1e293b' : '#f1f5f9', color: text, fontWeight: 700, fontSize: '14px', border: `1px solid ${border}`, cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </Box>
  );
}
